import api from './api';
import {
  ClubAddress as SeriesAddress,
  DartCloudTime,
  normalizeDetail,
} from './clubService';

export type { SeriesAddress, DartCloudTime };

export interface SeriesSchedule {
  type?: 'weekly' | 'biweekly' | 'monthly_weekday' | 'custom' | null;
  weekday?: number | null;
  weekdayPosition?: 'every' | 'first' | 'second' | 'third' | 'fourth' | 'last' | null;
  intervalWeeks?: number | null;
  seasonStart?: number | null;
  seasonEnd?: number | null;
  exceptions?: number[];
  fixedDates?: number[];
}

export interface CustomEntryField {
  id?: string;
  label?: string;
  type?: string;
  required?: boolean;
  options?: string[];
}

export interface Series {
  _id?: string;
  id?: string;
  name?: string;
  slug?: string;
  description?: string | null;
  image?: string | null;
  images?: string[];

  address?: SeriesAddress;
  email?: string | null;
  phone?: string | null;

  categories?: string[];
  tags?: string[];

  schedule?: SeriesSchedule | null;

  gameMode?: string | null;
  boards?: number | null;
  doorsOpen?: DartCloudTime | null;
  start?: DartCloudTime | null;
  entryDeadlineOnSite?: DartCloudTime | null;
  teamSize?: number | null;
  teamSizeMax?: number | null;

  entryEnabled?: boolean;
  entryFee?: number | null;
  entryLimit?: number | null;
  entryText?: string | null;
  entryLink?: string | null;
  entryBirthdayRequired?: boolean;
  entryEmailRequired?: boolean;
  entryPhoneRequired?: boolean;
  customEntryFields?: CustomEntryField[];
  customEntryHint?: string | null;
  showEntryList?: boolean;
  showWaitingList?: boolean;
  waitingEnabled?: boolean;
  leaveEntryAllowed?: boolean;
  registrationType?: 'DARTCLOUD' | 'EXTERNAL' | 'NONE' | null;
  requireConfirmation?: boolean | null;

  cancelled?: boolean;
  visible?: boolean;
  verified?: boolean;
  sponsored?: boolean;
  spam?: boolean;
  views?: number;

  owner?: string | null;
  admins?: string[];
  createdBy?: string | null;
  createdAt?: number;
  updatedAt?: number | null;
}

export interface SeriesEvent {
  _id?: string;
  id?: string;
  series?: string;
  date?: number;
  status?: 'SCHEDULED' | 'CANCELLED' | 'COMPLETED';
  startOverride?: DartCloudTime | null;
  doorsOpenOverride?: DartCloudTime | null;
  entryLimitOverride?: number | null;
  cancelledReason?: string | null;
  notes?: string | null;
  createdAt?: number;
  updatedAt?: number | null;
}

export interface SeriesDetailResponse {
  success: boolean;
  data: Series | null;
}

export interface SeriesEventsResponse {
  success: boolean;
  data: SeriesEvent[];
}

const WEEKDAY_NAMES_DE = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

const POSITION_NAMES_DE: Record<string, string> = {
  every: 'jeden',
  first: 'jeden ersten',
  second: 'jeden zweiten',
  third: 'jeden dritten',
  fourth: 'jeden vierten',
  last: 'jeden letzten',
};

class SeriesService {
  async getSeriesBySlug(slug: string): Promise<SeriesDetailResponse> {
    const res = await api.get(`/api/series/slug/${encodeURIComponent(slug)}`);
    return normalizeDetail<Series>(res.data);
  }

  async getSeriesById(id: string): Promise<SeriesDetailResponse> {
    const res = await api.get(`/api/series/id/${encodeURIComponent(id)}`);
    return normalizeDetail<Series>(res.data);
  }

  async getUpcomingEventsBySeries(seriesId: string): Promise<SeriesEventsResponse> {
    const res = await api.get(`/api/series/${encodeURIComponent(seriesId)}/events/upcoming`);
    return res.data;
  }

  async getEventsBySeries(seriesId: string): Promise<SeriesEventsResponse> {
    const res = await api.get(`/api/series/${encodeURIComponent(seriesId)}/events`);
    return res.data;
  }

  async createSeries(payload: any): Promise<any> {
    const res = await api.post('/api/series/create', payload);
    return res.data;
  }

  async updateSeries(payload: any): Promise<any> {
    const res = await api.post('/api/series/update', payload);
    return res.data;
  }

  async deleteSeries(id: string): Promise<any> {
    const res = await api.post('/api/series/delete', { id });
    return res.data;
  }

  async generateEvents(payload: any): Promise<any> {
    const res = await api.post('/api/series/events/generate', payload);
    return res.data;
  }

  async createEvent(payload: any): Promise<any> {
    const res = await api.post('/api/series/events/create', payload);
    return res.data;
  }

  async updateEvent(payload: any): Promise<any> {
    const res = await api.post('/api/series/events/update', payload);
    return res.data;
  }

  async cancelEvent(payload: any): Promise<any> {
    const res = await api.post('/api/series/events/cancel', payload);
    return res.data;
  }

  async deleteEvent(id: string): Promise<any> {
    const res = await api.post('/api/series/events/delete', { id });
    return res.data;
  }

  async deleteAllEvents(seriesId: string): Promise<any> {
    const res = await api.post('/api/series/events/delete-all', { seriesId });
    return res.data;
  }

  buildFileUrl(fileId?: string | null): string | null {
    if (!fileId) return null;
    if (fileId.startsWith('http://') || fileId.startsWith('https://')) return fileId;
    const base = api.defaults.baseURL || '';
    return `${base}/api/file/${fileId}`;
  }

  formatAddress(address?: SeriesAddress | null): string {
    if (!address) return 'Adresse nicht hinterlegt';
    if (address.label && address.label.trim().length > 0) return address.label;
    const parts: string[] = [];
    if (address.street) parts.push(address.street);
    const cityLine = [address.postcode, address.city].filter(Boolean).join(' ');
    if (cityLine) parts.push(cityLine);
    if (address.country) parts.push(address.country);
    return parts.length ? parts.join(', ') : 'Adresse nicht hinterlegt';
  }

  formatScheduleText(schedule?: SeriesSchedule | null): string {
    if (!schedule || !schedule.type) return 'Kein Zeitplan';
    const weekday =
      typeof schedule.weekday === 'number' &&
      schedule.weekday >= 0 &&
      schedule.weekday < 7
        ? WEEKDAY_NAMES_DE[schedule.weekday]
        : '';
    switch (schedule.type) {
      case 'weekly':
        return weekday ? `Jeden ${weekday}` : 'Wöchentlich';
      case 'biweekly':
        return weekday ? `Alle 2 Wochen ${weekday}` : 'Alle 2 Wochen';
      case 'monthly_weekday': {
        const pos = schedule.weekdayPosition
          ? POSITION_NAMES_DE[schedule.weekdayPosition]
          : '';
        if (pos && weekday) return `${pos} ${weekday} im Monat`;
        return weekday ? `Monatlich ${weekday}` : 'Monatlich';
      }
      case 'custom':
        return 'Individuelle Termine';
      default:
        return 'Termine';
    }
  }
}

export default new SeriesService();
